<script setup>
import { ref, computed } from 'vue'
import { noImage } from '@/common/helper'
import { useCabinetState } from '../composables/CabinetState'
import { useCatalogState } from '../composables/CatalogState'
import { useArticleStock } from '../composables/ArticleStock'
import { useArticleWeight } from '../composables/ArticleWeight'
import LocationView from './LocationView.vue'
import WeightScaleView from './WeightScaleView.vue'
import ArticleStockEdit from './ArticleStockEdit.vue'
import RelatedArticlesView from './RelatedArticlesView.vue'
import PushButton from '@/components/PushButton.vue'

const isScaleBanner = ref(true);

function showModal(item) {
  selectArticle();
}

const {
  rowsFiltered,
  allowImageFallback,
  nearestParentCategorySmallImageUrl,
  nearestParentCategoryLargeImageUrl,
  selected,
  selectedTrayItem,
  selectedTrayName,
  hasDeliveryFeature,
  hasRelatedArticles,
  isRelatedArticlesView,
  availableTrayOptions,
  availableTraySelect,
  hasParameterSelected,
  parametersSelected,
  isCabinetBorrowMode,
  canReturnArticle,
  canOpenTray,
  canOpenTrayStatus,
  canOpenTrayPermissions,
  isTakenByOtherUser,
  relatedStockCurrent,
  isLargeImageView,
  isLocationView,
  isLoadingLocation,
  hasLocation,
  closeModal,
  closeModalConditional,
  applyTrayUsage,
  showLocation,
  hideLocation,
  showRelatedArticles,
  hideRelatedArticles,
  prevRelatedStock,
  nextRelatedStock,
} = useCatalogState();

const {
  canApplyStock,
  isEditStockQuantity,
  pendingTransferQuantity,
  isStockMismatch,
  virtualStockQuantity,
  virtualStockQuantityState,
  hasEditCommentConditions,
  isCommentEdit,
  stockComment,
  canEditComment,
  cancelComment,
  saveComment,
  editComment,
  applyStockTransfer,
  undoStockTransfer,
  showRelatedStock,
  applyStock,
  cancelStock,
} = useArticleStock();

const {
  isScaleValueRequired,
  isScaleReady,
  isScaleCalibration,
  isItemWeightCalibrated,
  scaleLastValueIntegerFormatted,
  scaleItemsCountTaken,
  scaleItemsCountTakenFormatted,
  scaleItemsCountFormatted,
  scaleLastValueFormatted,
  scaleInsertWeightFormatted,
  scaleItemWeightCalibrated,
  setWeightScaleCalibrationMode,
} = useArticleWeight();

const {
  isReferencing,
  isOpening,
  isOpen,
  isPrevOpen,
  isClosing,
  openTray,
  closeTray,
} = useCabinetState();
</script>

<template>
  <!-- Article modal window. Note that "persistent not-escapable" class are replaced with additional conditions and handled by the function "closeModalConditional" -->
  <Modal id="articleWindow" size="3xl" class="non-escapable-modal dark:bg-gray-900 fix-padding-bottom" @close="(e) => closeModalConditional(e)" @click.outside="(e) => closeModalConditional(e)">
    <template #header>
      <div class="flex items-center text-lg dark:text-white">
        Artikel <span class="font-semibold ml-2" v-if="selected?.sku">{{ selected.sku }}</span>
      </div>
    </template>
    <template #body>

      <!-- Related articles -->
      <RelatedArticlesView v-if="isRelatedArticlesView && selected?.related" />

      <!-- Location -->
      <LocationView v-else-if="isLocationView" />

      <!-- Default view -->
      <div class="w-full dark:text-white" style="min-height: 250px; max-height: 1000px;" v-else>
        
        <!-- Article image, description, stock, comment -->
        <ArticleStockEdit />

        <!-- Weight scale panel -->
        <WeightScaleView v-if="isEditStockQuantity && !isCabinetBorrowMode" />

      </div>
    </template>
    <template #footer>
      <div class="flex justify-between">
        <Button :type="!isCommentEdit ? 'danger' : 'secondary'" :enabled="!isCommentEdit" @click="cancelStock()" v-if="isEditStockQuantity && isCabinetBorrowMode && false">Prekliči</Button>
        <Button type="secondary" @click="showLocation()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && !isLocationView && hasLocation">Lokacija</Button>
        <Button type="secondary" @click="showRelatedArticles()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && !isRelatedArticlesView && hasRelatedArticles && !hasLocation">Sorodni&nbsp;artikli</Button>
        <Button type="secondary" @click="hideLocation()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && isLocationView && hasLocation"><i class="fa fa-arrow-left inline mr-2"></i>Nazaj</Button>
        <Button type="secondary" @click="hideRelatedArticles()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && isRelatedArticlesView && !hasLocation"><i class="fa fa-arrow-left inline mr-2"></i>Nazaj</Button>
        <Button type="warning" @click="setWeightScaleCalibrationMode(true)" v-if="!isRelatedArticlesView && !isLargeImageView && isEditStockQuantity && !isCabinetBorrowMode && isScaleReady && !isScaleCalibration && canCalibrateWeightScale"><i class="fa fa-gear mr-2"></i>Umeri tehtnico</Button>
        <Button type="secondary" :enabled="false" v-if="!isRelatedArticlesView && !isLargeImageView && isEditStockQuantity && !isCabinetBorrowMode && isScaleReady && isScaleCalibration && canCalibrateWeightScale"><i class="fa fa-gear mr-2"></i>Umeri tehtnico</Button>

        <div class="flex justify-items-end w-full" style="min-height: 30px;">

          <!-- Middle block -->
          <div class="m-auto">
            <span v-if="isReferencing" class="middle-block-container">
              <Spinner size="6" style="display: inline-block;" />
              <span class="middle-block-message">Postavljam omaro v referenco...</span>
            </span>
            <span v-else-if="!isReferencing && isOpen && isClosing" class="middle-block-container">
              <Spinner size="6" style="display: inline-block;" />
              <span class="middle-block-message" v-if="hasDeliveryFeature">Pospravljam predal {{ selectedTrayName }}</span>
              <span class="middle-block-message" v-else>Zapiram predal {{ selectedTrayName }}</span>
            </span>
            <span v-else-if="!isReferencing && !isOpen && isOpening" class="middle-block-container">
              <Spinner size="6" style="display: inline-block;" />
              <span class="middle-block-message" v-if="hasDeliveryFeature">Dostavljam predal {{ selectedTrayName }}</span>
              <span class="middle-block-message" v-else>Odpiram predal {{ selectedTrayName }}</span>
            </span>
            <span v-else-if="!isReferencing && isOpen && !isPrevOpen && !isClosing && !isEditStockQuantity" class="middle-block-container">
              <i class="fa fa-lock-open text-green-500" style="position: relative; top: 2px;"></i> <span class="middle-block-message">{{ $t('catalog.tray_open_message') }}</span>
            </span>
            <span v-else-if="!isReferencing && isPrevOpen && !isOpening && !isClosing" class="middle-block-container">
              <i class="fa fa-triangle-exclamation text-amber-500" style="position: relative; top: 3px;"></i> <span class="middle-block-message">Za nadaljevanje najprej zapri prejšnji predal.</span>
            </span>
            <div v-else-if="isLocationView && relatedStockCurrent">
              <h3><span class="font-bold">{{ relatedStockCurrent.cabinet?.name }}</span><span class="font-normal" v-if="relatedStockCurrent.quantity > 0"> - zaloga {{ relatedStockCurrent.quantity }} kos</span></h3>
            </div>
            <div v-else-if="!isReferencing && isOpen && isScaleReady && isScaleValueRequired && isItemWeightCalibrated && !canApplyStock" class="animate-blink">
              <i class="fa fa-triangle-exclamation text-amber-800" style="position: relative; top: 3px;"></i> <span class="text-amber-900 middle-block-message">Tehtanje je obvezno!</span>
            </div>
            <div v-else-if="isEditStockQuantity && isCabinetBorrowMode && autoConfirm">
              <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-if="isCommentEdit">Potrdi urejanje komentarja</span>
              <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-else>Za nadaljevanje zapri predal</span>
            </div>
          </div>

          <!-- Tray selection -->
          <div v-if="!isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen && !hasLocation && selected.trays?.length > 0" class="mr-2">
            <DropDown :placeholder="$t('catalog.select_tray_placeholder')" :options="availableTrayOptions" v-model="availableTraySelect" :enabled="!isEditStockQuantity && availableTrayOptions.length > 1" class="w-80" style="min-width: 240px;" @change="onTraySelect()" />
          </div>

          <!-- Action buttons -->
          <div class="flex flex-row gap-2" v-if="!isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen && !hasLocation && selected.trays?.length > 0">
            <Button type="primary" :enabled="canOpenTray && canReturnArticle && canOpenTrayPermissions && canOpenTrayStatus" @click="openTray(selectedTrayItem)">{{ $t('catalog.open_tray') }}</Button>
          </div>
          <div class="flex flex-row gap-2" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && !(selected.trays?.length > 0)">
            <Button type="secondary" @click="closeModal()">{{ $t('generic.close') }}</Button>
          </div>
          <div class="flex flex-row gap-2" v-if="isEditStockQuantity && !(isEditStockQuantity && isCabinetBorrowMode)">
            <Button type="warning" :enabled="canApplyStock && !isScaleCalibration && isScaleAllowContinue" @click="startStockTransfer(selected)" v-if="!isReferencing && isOpen && !isCabinetBorrowMode && canTransferStock && !isStockTransferApplied && !isScaleCalibration && hasStockQuantityChanged && canApplyStock">Prenos zaloge</Button>
            <Button type="primary" :enabled="canApplyStock && !isScaleCalibration && isScaleAllowContinue" @click="applyStock(selected)">{{ $t('generic.confirm') }}</Button>
          </div>
          
          <!-- Take or return item -->
          <Button :type="selectedTrayUsage != 'taken' && !isCommentEdit ? 'primary' : 'secondary'" :enabled="true" class="ml-2" @click="applyTrayUsage('taken')" v-if="isEditStockQuantity && isCabinetBorrowMode && selectedTrayUsage != 'taken' && !isCommentEdit && !autoConfirm"><i class="fa fa-arrow-right-from-bracket mr-2"></i>Vzel</Button>
          <Button :type="selectedTrayUsage != 'present' && !isCommentEdit ? 'primary' : 'secondary'" :enabled="true" class="ml-2" @click="applyTrayUsage('present')" v-if="isEditStockQuantity && isCabinetBorrowMode && selectedTrayUsage != 'present' && !isCommentEdit && !autoConfirm"><i class="fa fa-arrow-right-to-bracket mr-2"></i>Vrnil</Button>

          <!-- Locations of related stock -->
          <div class="flex flex-row gap-2" v-if="isLocationView">
            <Button type="primary" @click="prevRelatedStock()" :enabled="relatedStockCount > 1 && relatedStockIndex - 1 >= 0"><i class="fa fa-chevron-left"></i></Button>
            <Button type="primary" @click="nextRelatedStock()" :enabled="relatedStockCount > 1 && relatedStockIndex + 1 < relatedStockCount"><i class="fa fa-chevron-right"></i></Button>
          </div>
      
        </div>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.middle-block-container {
  @apply text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300;
}

.middle-block-message {
  display: inline-block;
  position: relative;
  top: 3px;
  left: 10px;
}
</style>